import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Alert, CircularProgress, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { getLegalDocumentContent } from '../../services/legalDocumentService';
import styles from './LegalDocumentModal.module.scss';
const LEGAL_DOCUMENT_KEYS = {
    terms: 'TERMS',
    privacy: 'PRIVACY'
};
const LEGAL_DOCUMENT_API_TYPES = {
    terms: 'terms-of-use',
    privacy: 'privacy-policy'
};
const LegalDocumentModal = ({ documentType, onClose }) => {
    const { i18n, t } = useTranslation('translation', {
        keyPrefix: 'LEGAL_DOCUMENTS'
    });
    const documentKey = documentType ? LEGAL_DOCUMENT_KEYS[documentType] : null;
    const title = documentKey ? t(`${documentKey}.TITLE`) : '';
    const effectiveDate = documentKey ? t(`${documentKey}.EFFECTIVE_DATE`) : '';
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';
    const [documentContent, setDocumentContent] = React.useState(null);
    const [contentLoading, setContentLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState(false);
    const displayedTitle = (documentContent === null || documentContent === void 0 ? void 0 : documentContent.title) || title;
    const displayedEffectiveDate = loadError
        ? t('EFFECTIVE_DATE_LABEL')
        : (documentContent === null || documentContent === void 0 ? void 0 : documentContent.effectiveDate) || (documentContent === null || documentContent === void 0 ? void 0 : documentContent.effective_date) || effectiveDate;
    React.useEffect(() => {
        setDocumentContent(null);
        setContentLoading(false);
        setLoadError(false);
        if (!documentType)
            return;
        setContentLoading(true);
        getLegalDocumentContent(LEGAL_DOCUMENT_API_TYPES[documentType], locale)
            .then((content) => {
            setDocumentContent(content);
        })
            .catch((err) => {
            console.error('Failed to fetch legal document content:', err);
            setLoadError(true);
        })
            .finally(() => {
            setContentLoading(false);
        });
    }, [documentType, locale, t]);
    const renderParagraph = (paragraph) => {
        const lines = paragraph
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        if (lines.length <= 1) {
            return (_jsx("p", { className: styles.paragraph, children: paragraph }, paragraph));
        }
        const [firstLine, ...nestedLines] = lines;
        const hasLeadIn = firstLine.endsWith(':');
        return (_jsxs("div", { className: styles.paragraph_group, children: [hasLeadIn && _jsx("p", { className: styles.paragraph, children: firstLine }), _jsx("div", { className: hasLeadIn ? styles.nested_lines : styles.stacked_lines, children: (hasLeadIn ? nestedLines : lines).map((line, index) => (_jsx("p", { className: styles.paragraph, children: line }, `${line}-${index}`))) })] }, paragraph));
    };
    const renderSection = (section, index) => {
        var _a;
        if (section.type === 'heading') {
            const headingLevel = Math.min(6, Math.max(2, (_a = section.level) !== null && _a !== void 0 ? _a : 2));
            return (_jsx(Typography, { className: styles.section_heading, component: `h${headingLevel}`, children: section.text }, `${section.text}-${index}`));
        }
        if (section.type === 'list') {
            return (_jsx("ul", { className: styles.section_list, children: section.items.map((item, itemIndex) => (_jsx("li", { children: item }, `${item}-${itemIndex}`))) }, `list-${index}`));
        }
        return renderParagraph(section.text);
    };
    const getRenderableSections = (content) => {
        if (!content)
            return [];
        if (Array.isArray(content.sections) && content.sections.length > 0) {
            return content.sections;
        }
        if (Array.isArray(content.paragraphs) && content.paragraphs.length > 0) {
            return content.paragraphs.map((text) => ({ type: 'paragraph', text }));
        }
        const textContent = content.content || content.data;
        if (typeof textContent === 'string' && textContent.trim()) {
            return textContent
                .trim()
                .split(/\n\s*\n/)
                .map((text) => ({ type: 'paragraph', text: text.trim() }));
        }
        return [];
    };
    const renderableSections = getRenderableSections(documentContent);
    return (_jsx(Modal, { open: !!documentKey, onClose: onClose, className: styles.modal, slotProps: {
            backdrop: {
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)'
                }
            }
        }, children: _jsx("section", { className: `${styles.dialog} ${loadError ? styles.dialog_error : ''}`, role: "dialog", "aria-modal": "true", "aria-labelledby": "legal-document-title", children: documentKey && (_jsxs(_Fragment, { children: [_jsxs("header", { className: styles.header, children: [_jsxs("div", { className: styles.title_block, children: [_jsx(Typography, { id: "legal-document-title", className: styles.title, children: displayedTitle }), _jsx(Typography, { className: styles.effective_date, children: displayedEffectiveDate })] }), _jsx(IconButton, { className: styles.close_button, "aria-label": `${t('CLOSE_ARIA_LABEL')} ${displayedTitle}`, onClick: onClose, children: _jsx(CloseIcon, { fontSize: "small" }) })] }), _jsxs("div", { className: `${styles.body} ${loadError ? styles.body_error : ''}`, children: [contentLoading && (_jsx("div", { className: styles.status, children: _jsx(CircularProgress, { size: 28 }) })), !contentLoading && loadError && (_jsx(Alert, { severity: "error", variant: "outlined", icon: _jsx(BlockIcon, { fontSize: "small" }), className: styles.error_alert, children: t('LOAD_ERROR', { title: displayedTitle }) })), !contentLoading && !loadError && renderableSections.map(renderSection)] })] })) }) }));
};
export default LegalDocumentModal;
